<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Broadcast',
  },
})

const { can } = useAbility()

// ─── State ────────────────────────────────────────────────────────────────────
const search = ref('')
const filterChannel = ref(null)
const page = ref(1)
const perPage = ref(20)
const loading = ref(false)
const templates = ref([])
const totalItems = ref(0)

const deleteDialog = ref(false)
const selectedTemplate = ref(null)
const deleteLoading = ref(false)

// ─── Headers ──────────────────────────────────────────────────────────────────
const headers = [
  { title: 'Nama Template', key: 'name', sortable: false },
  { title: 'Channel', key: 'channel', sortable: false },
  { title: 'Tipe', key: 'type', sortable: false },
  { title: 'Status', key: 'is_active', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 100 },
]

const channelOptions = [
  { title: 'Semua', value: null },
  { title: 'WhatsApp', value: 'whatsapp' },
  { title: 'Email', value: 'email' },
  { title: 'WhatsApp & Email', value: 'both' },
]

const channelLabel = value => channelOptions.find(o => o.value === value)?.title ?? value

const typeLabels = {
  promo: 'Promo',
  ulang_tahun: 'Ulang Tahun',
  reminder: 'Reminder',
  custom: 'Custom',
}

// ─── Fetch ────────────────────────────────────────────────────────────────────
const fetchTemplates = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value
    if (filterChannel.value) query.channel = filterChannel.value

    const response = await useApi('/api/v1/broadcast-templates', { query }).get()
    const body = response?.data ?? response

    templates.value = body?.data ?? []
    totalItems.value = body?.pagination?.total ?? 0
  } catch (err) {
    console.error('[BroadcastTemplates] fetch error', err)
    templates.value = []
  } finally {
    loading.value = false
  }
}

watch([search, filterChannel, page], fetchTemplates, { immediate: true })

// ─── Delete ───────────────────────────────────────────────────────────────────
const confirmDelete = template => {
  selectedTemplate.value = template
  deleteDialog.value = true
}

const doDelete = async () => {
  deleteLoading.value = true
  try {
    await useApi(`/api/v1/broadcast-templates/${selectedTemplate.value.id}`).delete()
    deleteDialog.value = false
    fetchTemplates()
  } catch (err) {
    console.error('[BroadcastTemplates] delete error', err)
  } finally {
    deleteLoading.value = false
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardTitle class="pt-5 px-5">
          Template Broadcast
        </VCardTitle>
        <VCardText class="text-body-2 text-medium-emphasis px-5 pt-0">
          Template pesan untuk campaign broadcast WhatsApp/Email (Modul AI/AJ). Kirim ke pelanggan nyata
          dilakukan dari halaman Campaign, bukan di sini.
        </VCardText>

        <VCardText class="d-flex flex-wrap gap-4 align-center justify-space-between">
          <div class="d-flex gap-3 flex-wrap align-center">
            <VTextField
              v-model="search"
              placeholder="Cari nama template..."
              prepend-inner-icon="ri-search-line"
              density="compact"
              hide-details
              clearable
              style="min-width: 220px"
            />
            <VSelect
              v-model="filterChannel"
              :items="channelOptions"
              label="Channel"
              density="compact"
              hide-details
              style="min-width: 160px"
            />
          </div>

          <VBtn
            v-if="can('create', 'Broadcast')"
            :to="{ name: 'crm-broadcast-templates-create' }"
            prepend-icon="ri-add-line"
          >
            Tambah Template
          </VBtn>
        </VCardText>

        <VDivider />

        <VDataTableServer
          v-model:page="page"
          :headers="headers"
          :items="templates"
          :items-length="totalItems"
          :items-per-page="perPage"
          :loading="loading"
          no-data-text="Belum ada template broadcast."
        >
          <template #item.channel="{ item }">
            {{ channelLabel(item.channel) }}
          </template>

          <template #item.type="{ item }">
            {{ typeLabels[item.type] ?? item.type }}
          </template>

          <template #item.is_active="{ item }">
            <VChip
              :color="item.is_active ? 'success' : 'default'"
              size="small"
            >
              {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
            </VChip>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex gap-1 justify-center">
              <IconBtn
                v-if="can('update', 'Broadcast')"
                :to="{ name: 'crm-broadcast-templates-id-edit', params: { id: item.id } }"
              >
                <VIcon icon="ri-pencil-line" />
                <VTooltip activator="parent">
                  Edit
                </VTooltip>
              </IconBtn>
              <IconBtn
                v-if="can('delete', 'Broadcast')"
                color="error"
                @click="confirmDelete(item)"
              >
                <VIcon icon="ri-delete-bin-line" />
                <VTooltip activator="parent">
                  Hapus
                </VTooltip>
              </IconBtn>
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </VCol>
  </VRow>

  <VDialog
    v-model="deleteDialog"
    max-width="420"
    persistent
  >
    <VCard>
      <VCardTitle class="pt-5 px-5">
        Hapus Template?
      </VCardTitle>
      <VCardText class="px-5">
        Yakin ingin menghapus template <strong>{{ selectedTemplate?.name }}</strong>?
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn
          variant="tonal"
          @click="deleteDialog = false"
        >
          Batal
        </VBtn>
        <VBtn
          color="error"
          :loading="deleteLoading"
          @click="doDelete"
        >
          Hapus
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
